import React, { useState, useEffect, useCallback } from 'react';
import { Star, AlertTriangle, RefreshCw } from 'lucide-react';
import { Header } from '../components/Header';
import { Card, CardContent } from '../components/Card';
import { Button } from '../components/Button';
import { getFavoritesStats } from '../services/cloudFunctions';
import {
  PERIODS, parisToday, resolvePeriod, formatCount, formatFrDate, productLabel, viewState,
} from '../utils/favoritesStats';

const Figure = ({ label, value, hint }) => (
  <Card>
    <CardContent className="p-5">
      <p className="text-sm text-gray-600 mb-1">{label}</p>
      <p className="text-4xl font-black text-gray-800" title={value === null || value === undefined ? 'Indisponible' : undefined}>
        {formatCount(value)}
      </p>
      {hint && <p className="text-xs text-gray-500 mt-2">{hint}</p>}
    </CardContent>
  </Card>
);

const Ranking = ({ title, rows, valueKey, valueLabel, empty }) => (
  <Card>
    <CardContent className="p-0">
      <div className="px-5 py-3 border-b bg-gray-50">
        <h3 className="font-bold text-gray-800">{title}</h3>
      </div>
      {rows && rows.length > 0 ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-gray-500">
              <th className="px-5 py-2">#</th>
              <th className="px-5 py-2">Produit</th>
              <th className="px-5 py-2 text-right">{valueLabel}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row, index) => (
              <tr key={row.product_id}>
                <td className="px-5 py-2 text-gray-500">{index + 1}</td>
                <td className="px-5 py-2">{productLabel(row)}</td>
                <td className="px-5 py-2 text-right font-bold">{formatCount(row[valueKey])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="px-5 py-6 text-sm text-gray-500">{empty}</p>
      )}
    </CardContent>
  </Card>
);

export const FavoritesStatsView = ({ stats, state, errorMessage, onRetry }) => {
  if (state === 'loading') {
    return (
      <div className="flex items-center justify-center py-20" role="status" aria-label="Chargement">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  if (state === 'error') {
    return (
      <Card>
        <CardContent className="p-8 text-center" role="alert">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <p className="text-gray-800 font-bold">Statistiques indisponibles</p>
          <p className="text-gray-500 text-sm mt-1">{errorMessage || 'Le calcul a échoué.'}</p>
          {onRetry && <Button className="mt-4" onClick={onRetry}><RefreshCw className="w-4 h-4 mr-2 inline" />Réessayer</Button>}
        </CardContent>
      </Card>
    );
  }
  if (state === 'empty') {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">Aucun favori synchronisé pour l'instant</p>
          <p className="text-gray-400 text-sm mt-2">
            Les chiffres apparaîtront quand des clients utiliseront l'application 2.0.12 ou plus récente.
          </p>
        </CardContent>
      </Card>
    );
  }

  const { current, events, purchases, coverage } = stats;
  return (
    <div className="space-y-6">
      <Card className={stats.complete ? 'border border-green-200' : 'border border-amber-300 bg-amber-50'}>
        <CardContent className="p-4 text-sm space-y-1">
          <p className="font-bold text-gray-800">
            {stats.complete ? 'Mesure complète sur la période.' : 'Mesure incomplète — lire les limites ci-dessous.'}
          </p>
          <p className="text-gray-600">
            Période demandée : {formatFrDate(stats.period.start_date)} → {formatFrDate(stats.period.end_date)} ({stats.period.days} j, heure de Paris)
            {' · '}Début de la collecte : {stats.collection_started_on ? formatFrDate(stats.collection_started_on) : 'pas encore commencée'}
            {' · '}Calculé le {new Date(stats.computed_at).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}
          </p>
          {stats.limits.length > 0 && (
            <ul className="list-disc pl-5 text-amber-800">
              {stats.limits.map((limit) => <li key={limit}>{limit}</li>)}
            </ul>
          )}
        </CardContent>
      </Card>

      <section className="space-y-3">
        <h3 className="text-lg font-black text-gray-800">État actuel des favoris synchronisés</h3>
        <p className="text-sm text-gray-500">
          Photo d'aujourd'hui, indépendante de la période choisie. Un client ne compte qu'une fois par produit.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Figure
            label="Clients ayant des favoris synchronisés"
            value={current ? current.accounts_with_synced_favorites : null}
            hint={current ? `${formatCount(current.accounts_ever_synced)} comptes ont déjà synchronisé${stats.customers_total != null ? ` · ${formatCount(stats.customers_total)} clients inscrits` : ''}. Les clients restés sur une ancienne version ne sont pas mesurés (ce ne sont pas des clients à zéro favori).` : 'Calcul indisponible.'}
          />
          <Figure
            label="Relations client–produit actives"
            value={current ? current.active_relations : null}
            hint="Nombre total de favoris actifs, tous clients confondus."
          />
        </div>
        <Ranking
          title="Produits les plus souvent en favori aujourd'hui (nombre de clients)"
          rows={current ? current.ranking : null}
          valueKey="accounts"
          valueLabel="Clients"
          empty={current ? 'Aucun favori actif.' : 'Classement indisponible (calcul incomplet).'}
        />
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-black text-gray-800">Ajouts et retraits sur la période</h3>
        {events ? (
          <>
            <p className="text-sm text-gray-500">
              Changements effectifs enregistrés par le serveur du {formatFrDate(events.covered_from)} au {formatFrDate(events.covered_to)}
              {coverage.events === 'partial' ? ' (période partiellement couverte)' : ''}. La date est celle de réception par le serveur, pas toujours celle du geste (hors ligne).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Figure label="Ajouts" value={events.adds} />
              <Figure label="Retraits" value={events.removes} />
              <Figure label="Reprises d'anciens favoris" value={events.imports} hint="Comptées à part : ce ne sont pas de nouveaux ajouts." />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Ranking title="Produits les plus ajoutés" rows={events.top_added} valueKey="adds" valueLabel="Ajouts" empty="Aucun ajout sur la période." />
              <Ranking title="Produits les plus retirés" rows={events.top_removed} valueKey="removes" valueLabel="Retraits" empty="Aucun retrait sur la période." />
            </div>
          </>
        ) : (
          <Card><CardContent className="p-5 text-sm text-gray-600">
            Non mesuré : aucune collecte sur cette période (la collecte n'avait pas encore commencé). Ce n'est pas un zéro.
          </CardContent></Card>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-black text-gray-800">Commandes avec un produit déjà en favori</h3>
        {purchases ? (
          <>
            <p className="text-sm text-gray-500">
              Commandes de la période, payées et terminées, contenant au moins un produit que le client avait déjà en favori avant de commander.
              C'est une association observée, pas des ventes « générées » par les favoris.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Figure
                label={purchases.orders_with_prior_favorite_is_lower_bound ? 'Commandes (au moins)' : 'Commandes'}
                value={purchases.orders_with_prior_favorite}
                hint={`Sur ${formatCount(purchases.eligible_orders)} commandes payées et terminées ; preuve disponible pour ${formatCount(purchases.orders_with_proof)}.`}
              />
              <Figure label="dont remboursées" value={purchases.refunded_among_them} hint="Incluses, comme dans le chiffre d'affaires." />
              <Figure label="Annulées (exclues)" value={purchases.cancelled_orders_excluded} />
            </div>
          </>
        ) : (
          <Card><CardContent className="p-5 text-sm text-gray-600">Indisponible : trop de commandes pour la borne de lecture. Réduisez la période.</CardContent></Card>
        )}
      </section>

      <ul className="text-xs text-gray-400 list-disc pl-5">
        {stats.notes.map((note) => <li key={note}>{note}</li>)}
      </ul>
    </div>
  );
};

const messageFor = (error) => {
  const code = error && error.code;
  if (code === 'functions/permission-denied') return "Accès réservé aux administrateurs.";
  if (code === 'functions/unauthenticated') return 'Session expirée : reconnectez-vous.';
  if (code === 'functions/invalid-argument') return 'Période invalide (62 jours au plus).';
  if (code === 'functions/failed-precondition') return 'Statistiques des favoris temporairement désactivées.';
  return 'Le calcul a échoué. Réessayez dans un instant.';
};

export const FavoritesStats = () => {
  const [period, setPeriod] = useState('30d');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const range = resolvePeriod(period, parisToday(), custom);
    if (!range) {
      if (period === 'custom') { setLoading(false); setStats(null); setError({ code: 'functions/invalid-argument' }); }
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setStats(await getFavoritesStats({ ...range, top: 10 }));
    } catch (e) {
      setStats(null);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [period, custom]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <Header title="⭐ Favoris" subtitle="Favoris synchronisés des clients (application 2.0.12 et plus)" />
      <div className="p-6 space-y-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex gap-2 flex-wrap">
              {PERIODS.map((option) => (
                <Button key={option.value} size="sm" variant={period === option.value ? 'primary' : 'outline'} onClick={() => setPeriod(option.value)}>
                  {option.label}
                </Button>
              ))}
            </div>
            {period === 'custom' && (
              <div className="flex gap-4 mt-4">
                <label className="flex-1 text-sm font-medium">Date début
                  <input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="w-full px-3 py-2 border rounded-lg mt-1" />
                </label>
                <label className="flex-1 text-sm font-medium">Date fin
                  <input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="w-full px-3 py-2 border rounded-lg mt-1" />
                </label>
              </div>
            )}
          </CardContent>
        </Card>
        <FavoritesStatsView
          stats={stats}
          state={viewState({ loading, error, stats })}
          errorMessage={error ? messageFor(error) : null}
          onRetry={load}
        />
      </div>
    </div>
  );
};
